/**
 * Query Configuration for React Query
 *
 * Defines staleTime and gcTime for different data types.
 * Aggressive caching to reduce API calls - data is cached for longer periods.
 */

// Time constants in milliseconds
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

/**
 * Query cache configuration by data type
 *
 * staleTime: How long data is considered fresh (won't refetch)
 * gcTime: How long to keep in cache after becoming unused
 */
export const QUERY_CACHE_CONFIG = {
  // Static/Reference Data - rarely changes (2 hours)
  static: {
    staleTime: 2 * HOUR,
    gcTime: 4 * HOUR,
  },

  // Semi-static data - changes occasionally (1 hour)
  semiStatic: {
    staleTime: 1 * HOUR,
    gcTime: 2 * HOUR,
  },

  // Dynamic data - changes frequently (30 min)
  dynamic: {
    staleTime: 30 * MINUTE,
    gcTime: 1 * HOUR,
  },

  // Dashboard/Stats data - needs fresher updates (15 min)
  dashboard: {
    staleTime: 15 * MINUTE,
    gcTime: 30 * MINUTE,
  },

  // Real-time data - short cache (5 min) + polling
  realtime: {
    staleTime: 5 * MINUTE,
    gcTime: 10 * MINUTE,
  },

  // Device status - very short cache + polling
  deviceStatus: {
    staleTime: 30 * 1000,  // 30 seconds
    gcTime: 2 * MINUTE,
    refetchInterval: 30 * 1000,  // Poll every 30s
  },
} as const;

/**
 * Query key factory for consistent cache keys
 */
export const queryKeys = {
  // Customers
  customers: {
    all: ['customers'] as const,
    detail: (id: string) => ['customers', id] as const,
  },

  // People
  people: {
    all: (customerId: string) => ['people', customerId] as const,
    detail: (id: string) => ['people', 'detail', id] as const,
    byRole: (customerId: string, roleType: string) => ['people', customerId, 'role', roleType] as const,
  },

  // Zones
  zones: {
    all: (customerId: string) => ['zones', customerId] as const,
    detail: (id: string) => ['zones', 'detail', id] as const,
  },

  // Devices
  devices: {
    all: (customerId: string) => ['devices', customerId] as const,
    detail: (id: string) => ['devices', 'detail', id] as const,
    status: (customerId: string) => ['devices', customerId, 'status'] as const,
  },

  // Access
  accessLogs: {
    all: (customerId: string) => ['accessLogs', customerId] as const,
    recent: (customerId: string) => ['accessLogs', customerId, 'recent'] as const,
  },
  accessProfiles: {
    all: (customerId: string) => ['accessProfiles', customerId] as const,
  },
  accessRules: {
    all: (customerId: string) => ['accessRules', customerId] as const,
  },

  // Dashboard
  dashboard: {
    stats: (customerId: string) => ['dashboard', 'stats', customerId] as const,
    globalStats: () => ['dashboard', 'globalStats'] as const,
  },

  // Static Reference Data
  roles: {
    all: () => ['roles'] as const,
  },
  permissions: {
    all: () => ['permissions'] as const,
  },

  // Organization Structure
  departments: {
    all: (customerId: string) => ['departments', customerId] as const,
  },
  blocks: {
    all: (customerId: string) => ['blocks', customerId] as const,
  },
  units: {
    all: (customerId: string) => ['units', customerId] as const,
  },

  // Users
  users: {
    all: () => ['users'] as const,
    byCustomer: (customerId: string) => ['users', customerId] as const,
    assignments: (userId: string) => ['users', userId, 'assignments'] as const,
  },
} as const;

/**
 * Get cache config for a specific data type
 */
export const getCacheConfig = (type: keyof typeof QUERY_CACHE_CONFIG) => {
  return QUERY_CACHE_CONFIG[type];
};
